<template>
  <main class="blog-view">
    <section class="blog-hero">
      <div class="blog-hero__inner">
        <p class="blog-hero__eyebrow">Blog Mes Meilleurs Menus</p>
        <h1 class="blog-hero__title">{{ pageTitle }}</h1>
        <!-- <p class="blog-hero__subtitle">{{ pageDescription }}</p> -->
      </div>
    </section>

    <section class="blog-section">
      <div class="blog-section__inner">
        <div class="blog-layout">
          <div class="blog-content">
            <section class="blog-toolbar" aria-label="Informations du listing">
              <div>
                <p class="blog-toolbar__count">
                  {{ totalResults }} article<span v-if="totalResults !== 1">s</span>
                </p>
                <p class="blog-toolbar__page">Page {{ currentPage }} sur {{ totalPages }}</p>
              </div>
            </section>

            <LoaderView v-if="loading" text="Chargement des articles..." />

            <div v-else-if="categoryNotFound" class="blog-empty">
              La catégorie demandée est introuvable ou indisponible.
            </div>

            <div v-else-if="articles.length === 0" class="blog-empty">
              Aucun article ne correspond à votre recherche.
            </div>

            <div v-else class="blog-grid">
              <article v-for="article in articles" :key="article.slug" class="blog-card">
                <router-link
                  class="blog-card__link"
                  :to="{ name: 'blog-article', params: { articleSlug: article.slug } }"
                >
                  <div class="blog-card__media">
                    <img
                      v-if="article.featured_image"
                      :src="mediaSrc(article.featured_image)"
                      :alt="article.title"
                      class="blog-card__image"
                    />
                    <div v-else class="blog-card__image blog-card__image--fallback">
                      {{ article.title }}
                    </div>
                  </div>

                  <div class="blog-card__content">
                    <p class="blog-card__category">{{ article.category.title }}</p>
                    <h2 class="blog-card__title">{{ article.title }}</h2>
                    <p class="blog-card__excerpt">{{ article.excerpt }}</p>
                  </div>
                </router-link>
              </article>
            </div>

            <nav v-if="!loading && totalPages > 1" class="blog-pagination" aria-label="Pagination du blog">
              <button
                type="button"
                class="blog-pagination__button"
                :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)"
              >
                Précédent
              </button>

              <button
                v-for="page in visiblePages"
                :key="page"
                type="button"
                class="blog-pagination__button"
                :class="{ 'blog-pagination__button--active': page === currentPage }"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>

              <button
                type="button"
                class="blog-pagination__button"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
              >
                Suivant
              </button>
            </nav>
          </div>

          <BlogSidebar
            :categories="categories"
            :recent-articles="recentArticles"
            :search-value="searchQuery"
            :current-search-query="searchQuery"
            :active-category-slug="categorySlug"
            @search="handleSearch"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { api } from "@/api";
import BlogSidebar from "@/components/BlogSidebar.vue";
import LoaderView from "@/components/LoaderView.vue";

export default {
  name: "BlogListView",
  components: {
    BlogSidebar,
    LoaderView,
  },
  data() {
    return {
      loading: true,
      categoryNotFound: false,
      articles: [],
      categories: [],
      recentArticles: [],
      currentCategory: null,
      totalResults: 0,
      currentPage: 1,
      totalPages: 1,
    };
  },
  computed: {
    categorySlug() {
      return this.$route.params.categorySlug || "";
    },
    searchQuery() {
      return String(this.$route.query.q || "").trim();
    },
    pageNumber() {
      const parsedValue = Number.parseInt(this.$route.query.page, 10);
      if (Number.isInteger(parsedValue) && parsedValue > 0) {
        return parsedValue;
      }
      return 1;
    },
    pageTitle() {
      if (this.categoryNotFound) {
        return "Catégorie introuvable";
      }

      if (this.currentCategory && this.searchQuery) {
        return `${this.currentCategory.title} : résultats pour "${this.searchQuery}"`;
      }

      if (this.currentCategory) {
        return this.currentCategory.title;
      }

      if (this.searchQuery) {
        return `Résultats du blog pour "${this.searchQuery}"`;
      }

      return "Tous nos articles nutrition, santé et climat";
    },
    pageDescription() {
      if (this.currentCategory && this.currentCategory.description) {
        if (this.searchQuery) {
          return `${this.currentCategory.description} Résultats filtrés pour "${this.searchQuery}".`;
        }
        return this.currentCategory.description;
      }

      if (this.searchQuery) {
        return `Explorez les articles du blog Mes Meilleurs Menus correspondant à la recherche "${this.searchQuery}".`;
      }

      return "Conseils pratiques, nutrition, recettes, saisonnalité et alimentation durable pour mieux manger au quotidien.";
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
  },
  watch: {
    "$route.fullPath": {
      immediate: true,
      handler() {
        this.loadPage();
      },
    },
  },
  methods: {
    loadPage() {
      this.loading = true;
      this.categoryNotFound = false;

      Promise.all([this.loadArticles(), this.loadSidebar()])
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.updateHead();
        });
    },
    loadArticles() {
      return api
        .get("/blog/articles/", {
          params: {
            page: this.pageNumber,
            q: this.searchQuery || undefined,
            category: this.categorySlug || undefined,
          },
        })
        .then((response) => {
          const payload = response.data || {};
          this.articles = Array.isArray(payload.results) ? payload.results : [];
          this.totalResults = Number(payload.total_results) || 0;
          this.currentPage = Number(payload.current_page) || 1;
          this.totalPages = Number(payload.total_pages) || 1;
          this.currentCategory = payload.current_category || null;
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.categoryNotFound = true;
            this.articles = [];
            this.totalResults = 0;
            this.currentPage = 1;
            this.totalPages = 1;
            this.currentCategory = null;
            return;
          }

          throw error;
        });
    },
    loadSidebar() {
      return Promise.all([
        api.get("/blog/categories/"),
        api.get("/blog/articles/recent/"),
      ]).then(([categoriesResponse, recentResponse]) => {
        this.categories = Array.isArray(categoriesResponse.data) ? categoriesResponse.data : [];
        this.recentArticles = Array.isArray(recentResponse.data) ? recentResponse.data : [];
      });
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }

      this.$router.push({
        name: this.categorySlug ? "blog-category" : "blog-list",
        params: this.categorySlug ? { categorySlug: this.categorySlug } : {},
        query: this.buildRouteQuery(page, this.searchQuery),
      });
    },
    handleSearch(query) {
      this.$router.push({
        name: this.categorySlug ? "blog-category" : "blog-list",
        params: this.categorySlug ? { categorySlug: this.categorySlug } : {},
        query: this.buildRouteQuery(1, query),
      });
    },
    buildRouteQuery(page, query) {
      const routeQuery = {};

      if (query) {
        routeQuery.q = query;
      }

      if (page > 1) {
        routeQuery.page = String(page);
      }

      return routeQuery;
    },
    updateHead() {
      const title = `${this.pageTitle} | Blog Mes Meilleurs Menus`;
      const description = this.pageDescription;
      const canonicalUrl = `${window.location.origin}${this.$route.fullPath}`;
      const imageUrl = `${window.location.origin}/favicon.png`;

      document.title = title;
      this.setMetaTag("description", description);
      this.setMetaTag("og:title", title, true);
      this.setMetaTag("og:description", description, true);
      this.setMetaTag("og:type", "website", true);
      this.setMetaTag("og:url", canonicalUrl, true);
      this.setMetaTag("og:image", imageUrl, true);
      this.setMetaTag("twitter:card", "summary_large_image");
      this.setMetaTag("twitter:title", title);
      this.setMetaTag("twitter:description", description);
      this.setMetaTag("twitter:image", imageUrl);
      this.setCanonicalLink(canonicalUrl);
      this.removeJsonLd();
    },
    setMetaTag(name, content, isProperty = false) {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.head.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(isProperty ? "property" : "name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    },
    setCanonicalLink(url) {
      let tag = document.head.querySelector('link[rel="canonical"]');

      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
      }

      tag.setAttribute("href", url);
    },
    removeJsonLd() {
      const existing = document.head.querySelector("#blog-json-ld");
      if (existing) {
        existing.remove();
      }
    },
    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
  },
};
</script>

<style scoped>
.blog-view {
  padding-top: 110px;
  padding-bottom: 48px;
}

.blog-hero {
  padding: 40px 20px 24px;
}

.blog-hero__inner,
.blog-section__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.blog-hero__eyebrow {
  margin: 0 0 8px;
  color: #e57b57;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.blog-hero__title {
  margin: 0;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(36px, 4vw, 60px);
  line-height: 1.05;
}

.blog-hero__subtitle {
  max-width: 760px;
  margin: 16px 0 0;
  color: #35524a;
  font-size: 18px;
  line-height: 1.6;
}

.blog-section {
  padding: 0 20px;
}

.blog-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 32px;
  align-items: start;
}

.blog-content {
  min-width: 0;
}

.blog-toolbar {
  margin-bottom: 24px;
}

.blog-toolbar__count,
.blog-toolbar__page {
  margin: 0;
  color: #35524a;
}

.blog-toolbar__count {
  color: #004c40;
  font-size: 18px;
  font-weight: 700;
}

.blog-toolbar__page {
  margin-top: 4px;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.blog-card__link {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 76, 64, 0.08);
  color: inherit;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.blog-card__link:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 36px rgba(0, 76, 64, 0.14);
}

.blog-card__media {
  aspect-ratio: 4 / 3;
  background: #e8efe8;
}

.blog-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.blog-card__image--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.9), rgba(0, 76, 64, 0.9));
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

.blog-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 20px;
}

.blog-card__category {
  margin: 0 0 10px;
  color: #e57b57;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.blog-card__title {
  margin: 0 0 12px;
  color: #004c40;
  font-size: 22px;
  line-height: 1.25;
}

.blog-card__excerpt {
  margin: 0;
  color: #4a5f58;
  font-size: 15px;
  line-height: 1.6;
}

.blog-empty {
  padding: 48px 24px;
  border-radius: 18px;
  background: #f5f1e8;
  color: #35524a;
  text-align: center;
}

.blog-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
}

.blog-pagination__button {
  min-width: 44px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
  cursor: pointer;
}

.blog-pagination__button--active {
  border-color: #004c40;
  background: #004c40;
  color: #fff;
}

.blog-pagination__button:disabled {
  opacity: 0.45;
  cursor: default;
}

@media only screen and (max-width: 1023px) {
  .blog-layout {
    grid-template-columns: 1fr;
  }

  .blog-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 899px) {
  .blog-view {
    padding-top: 90px;
  }
}

@media only screen and (max-width: 599px) {
  .blog-hero,
  .blog-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .blog-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .blog-card__content {
    padding: 18px;
  }

  .blog-card__title {
    font-size: 20px;
  }
}
</style>
